import { useEffect, useRef, useState } from "react";

/* Page-level behaviours shared by the create-order desks. Each one is a straight lift of the
   forward page's effect, keyed by the same inputs; none touches form state. */

// Keep the active step pill in view when the row scrolls horizontally (mobile),
// so you always see where you are. No-op on desktop where every pill fits.
export function useStepRowAutoScroll(stepRowRef, step) {
  useEffect(() => {
    const row = stepRowRef.current;
    if (!row) return;
    const active = row.querySelector(`[data-step="${step}"]`);
    if (!active) return;
    const rowRect = row.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    const delta = (aRect.left - rowRect.left) - (row.clientWidth - active.clientWidth) / 2;
    if (Math.abs(delta) > 4) row.scrollTo({ left: Math.max(0, row.scrollLeft + delta), behavior: "smooth" });
  }, [step, stepRowRef]);
}

// "More below" cue: on every step, show a hint while form content still extends past
// the visible area (i.e. behind/under the sticky footer). Recomputes on scroll, resize
// and content growth (courier rates loading, item rows added, groups expanding).
export function useMoreBelowCue({ stepBodyRef, footerRef, step }) {
  const [moreBelow, setMoreBelow] = useState(false); // is there form content past the fold?
  const moreBelowComputeRef = useRef(() => {}); // latest cue compute — callable from inner-list observers
  useEffect(() => {
    const compute = () => {
      const body = stepBodyRef.current;
      if (!body) { setMoreBelow(false); return; }
      const footerH = footerRef.current ? footerRef.current.offsetHeight : 64;
      const foldY = window.innerHeight - footerH + 24;
      // (1) the step body itself runs past the fold, behind the sticky footer…
      let more = body.getBoundingClientRect().bottom > foldY;
      // (2) …or a scroller inside it (the saved-locations list caps at 52vh) still hides rows
      //     below its own edge — the page fits, but the merchant can't see everything.
      if (!more) {
        for (const el of body.querySelectorAll(".overflow-y-auto, .overflow-auto")) {
          if (el.scrollHeight - el.scrollTop - el.clientHeight > 4 && el.getBoundingClientRect().top < foldY) { more = true; break; }
        }
      }
      setMoreBelow(more);
    };
    moreBelowComputeRef.current = compute;
    compute();
    window.addEventListener("scroll", compute, true); // capture → catches window or inner scrollers
    window.addEventListener("resize", compute);
    let ro;
    if (stepBodyRef.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(compute);
      ro.observe(stepBodyRef.current);
    }
    const t = setTimeout(compute, 360); // after the step-enter animation settles
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
      if (ro) ro.disconnect();
      clearTimeout(t);
    };
  }, [step, stepBodyRef, footerRef]);

  const scrollForMore = () => {
    const body = stepBodyRef.current;
    const footerH = footerRef.current ? footerRef.current.offsetHeight : 64;
    const foldY = window.innerHeight - footerH + 24;
    // Same priority as the cue: when the page already fits, the hidden content is inside a
    // scroller within the step (the saved-locations list) — scroll that, not the window.
    if (body && body.getBoundingClientRect().bottom <= foldY) {
      const inner = [...body.querySelectorAll(".overflow-y-auto, .overflow-auto")]
        .find((el) => el.scrollHeight - el.scrollTop - el.clientHeight > 4);
      if (inner) { inner.scrollBy({ top: Math.round(inner.clientHeight * 0.72), behavior: "smooth" }); return; }
    }
    let n = body ? body.parentElement : null, scroller = null;
    while (n) {
      const oy = getComputedStyle(n).overflowY;
      if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight + 4) { scroller = n; break; }
      n = n.parentElement;
    }
    const by = Math.round(window.innerHeight * 0.72);
    (scroller || window).scrollBy({ top: by, behavior: "smooth" });
  };

  return { moreBelow, scrollForMore, moreBelowComputeRef };
}

// A focused <input type="number"> silently changes its value on a mouse-wheel scroll —
// a real data-integrity hazard on price / quantity / weight / dimension fields. Blur any
// focused number field the instant a wheel scroll starts, so the value can never change
// by accident; the page keeps scrolling as normal. Covers every number field, every stage.
export function useNumberWheelBlur() {
  useEffect(() => {
    const onWheel = () => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number") el.blur();
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);
}
